/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useState } from 'react';

import { AudioWaveRecorder } from 'components/atoms/audio_wave';

import { useAudioRecorderController } from 'hooks/useAudioRecorderController';
import type { CustomAnalysisPrompt } from 'hooks/useCustomAnalysisPrompt';

import {
  improveCustomAnalysis,
  saveCustomAnalysis,
} from 'services/actions/ai_management_custom_analysis';
import { speechToText } from 'services/actions/chatbot_actions';

import iconIaGradiente from 'assets/images/icone-ia-gradiente.svg';
import iconRobotIa from 'assets/images/icon_robot_ia.svg';

import { Overlay, Panel } from './styles';

const HINT =
  'Oriente o Analista de Editais sobre o que é mais importante para você. Ele utilizará essas ' +
  'instruções para complementar a análise automática do edital.';

const PLACEHOLDER =
  'Exemplo: Verifique exigências de certificações ISO, destaque cláusulas que possam gerar riscos ' +
  'contratuais, confirme se há benefícios para ME/EPP, identifique prazos críticos e apresente um ' +
  'resumo das obrigações da contratada.';

interface BiddingSummaryCustomAnalysisDrawerProps {
  prompt: CustomAnalysisPrompt | null;
  onClose: () => void;
  onSaved: () => void;
}

const BiddingSummaryCustomAnalysisDrawer: React.FC<BiddingSummaryCustomAnalysisDrawerProps> = ({
  prompt,
  onClose,
  onSaved,
}) => {
  const [text, setText] = useState(prompt?.content ?? '');
  const [saving, setSaving] = useState(false);
  const [improving, setImproving] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { waveRef, isRecording, startRecording, cancelRecording, stopRecording } =
    useAudioRecorderController();

  const isBusy = saving || improving || transcribing || isRecording;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleFinishRecording = useCallback(async (blob: Blob) => {
    const formData = new FormData();
    formData.append('audio', blob, 'audio.webm');

    try {
      setTranscribing(true);
      const response = await speechToText(formData);
      const transcrito = response?.data?.text;

      if (response.status === 200 && transcrito) {
        setText((prev) => (prev ? `${prev} ${transcrito}` : transcrito).trim());
      }
    } catch (err) {
      console.error('Erro na transcrição do ditado:', err);
      setErrorMessage('Não foi possível transcrever o áudio. Tente novamente.');
    } finally {
      setTranscribing(false);
    }
  }, []);

  const handleImprove = useCallback(() => {
    const current = text.trim();
    if (!current || isBusy) return;

    setImproving(true);
    setErrorMessage('');

    improveCustomAnalysis(current)
      .then((response) => {
        const improved =
          response?.data?.prompt ?? response?.data?.content ?? response?.data?.improved_prompt;

        if (improved?.trim()) setText(improved);
        else setErrorMessage('A IA não retornou um texto aprimorado. Tente novamente.');
      })
      .catch((err) => {
        console.error('Erro ao aprimorar a análise personalizada:', err);
        setErrorMessage('Não foi possível aprimorar o prompt. Tente novamente.');
      })
      .finally(() => setImproving(false));
  }, [text, isBusy]);

  const handleSave = useCallback(() => {
    if (isBusy) return;

    setSaving(true);
    setErrorMessage('');

    saveCustomAnalysis(text.trim())
      .then(() => {
        onSaved();
        onClose();
      })
      .catch((err) => {
        console.error('Erro ao salvar a análise personalizada:', err);
        setErrorMessage('Não foi possível salvar a análise personalizada. Tente novamente.');
      })
      .finally(() => setSaving(false));
  }, [text, isBusy, onSaved, onClose]);

  const handleClose = () => {
    cancelRecording();
    onClose();
  };

  return (
    <Overlay onClick={handleClose}>
      <Panel onClick={(event) => event.stopPropagation()}>
        <div className="drawer-content">
          <div className="drawer-head">
            <img src={iconRobotIa} alt="" aria-hidden className="drawer-head-icon" />
            <button
              type="button"
              className="drawer-close"
              onClick={handleClose}
              aria-label="Fechar"
            >
              <span className="material-icons">close</span>
            </button>
          </div>

          <p className="drawer-hint">{HINT}</p>

          <div className="dictate-row">
            {isRecording && (
              <div className="dictate-wave">
                <AudioWaveRecorder ref={waveRef} variant="white" />
              </div>
            )}

            {isRecording || transcribing ? (
              <div className="dictate-controls">
                <button
                  type="button"
                  className="dictate-cancel"
                  onClick={cancelRecording}
                  disabled={transcribing}
                  aria-label="Cancelar ditado"
                >
                  <span className="material-icons">close</span>
                </button>
                <button
                  type="button"
                  className="dictate-confirm"
                  onClick={() => stopRecording(handleFinishRecording)}
                  disabled={transcribing}
                  aria-label="Concluir ditado"
                >
                  {transcribing ? (
                    <span className="dictate-transcribing">Transcrevendo…</span>
                  ) : (
                    <span className="material-icons">check</span>
                  )}
                </button>
              </div>
            ) : (
              <button type="button" className="dictate" onClick={startRecording} disabled={isBusy}>
                <span className="material-icons">mic</span>
                Ditar texto
              </button>
            )}
          </div>

          <textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={PLACEHOLDER}
            disabled={improving}
            aria-label="Instruções para o Analista de Editais"
          />

          <div>
            <button
              type="button"
              className="btn btn-outline-primary btn-outline-ia improve-button d-inline-flex align-items-center"
              onClick={handleImprove}
              disabled={isBusy || !text.trim()}
            >
              <img src={iconIaGradiente} alt="" aria-hidden className="improve-icon" />
              <span className="span-outline-ia">
                {improving ? 'Aprimorando…' : 'Aprimorar meu prompt'}
              </span>
            </button>
          </div>
        </div>

        <div className="drawer-footer">
          {errorMessage ? (
            <p className="drawer-error" role="alert">
              {errorMessage}
            </p>
          ) : null}

          <button type="button" className="drawer-save" onClick={handleSave} disabled={isBusy}>
            {saving ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </Panel>
    </Overlay>
  );
};

export default BiddingSummaryCustomAnalysisDrawer;
